import { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Table, Badge, Spinner, Form } from 'react-bootstrap';
import { 
    FaPlus, 
    FaEdit, 
    FaEye, 
    FaTrash, 
    FaToggleOn, 
    FaToggleOff, 
    FaThLarge, 
    FaList, 
    FaUserCircle,
    FaCalendarAlt,
    FaCalendarCheck,
    FaCalendarTimes
} from 'react-icons/fa';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// Import Firebase service instead of API
import { getEmployees, deleteEmployee, updateEmployeeStatus } from '../services/firebase/employeeService';
import { toast } from 'react-toastify';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import DepartmentSelector from '../components/common/DepartmentSelector';
import { isDeptVisibleToUser } from '../utils/departmentVisibility';
import '../assets/css/employee.css';

const formatDate = (dateValue) => {
    if (!dateValue) return '—';
    try {
        if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
            return moment(dateValue.toDate()).format('DD MMM YYYY');
        }
        if (dateValue?.seconds) {
            return moment.unix(dateValue.seconds).format('DD MMM YYYY');
        }
        if (dateValue?._seconds) {
            return moment.unix(dateValue._seconds).format('DD MMM YYYY');
        }
        if (dateValue instanceof Date) {
            if (isNaN(dateValue.getTime())) return '—';
            return moment(dateValue).format('DD MMM YYYY');
        }
        if (typeof dateValue === 'string') {
            const s = dateValue.trim();
            if (!s || s === '-' || s === 'N/A' || s === '—') return '—';
            const m = moment(s);
            if (m.isValid()) {
                return m.format('DD MMM YYYY');
            }
        }
        return '—';
    } catch {
        return '—';
    }
};

const EmployeeListPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isPrivilegedUser = user?.role === 'hr' || user?.role === 'admin' || user?.role === 'manager';

    const [employees, setEmployees] = useState([]);
    const [allRawEmployees, setAllRawEmployees] = useState([]);
    const [counts, setCounts] = useState({ active: 0, inactive: 0, total: 0 });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        department: '',
        designation: '',
        status: location.state?.filter === 'incomplete' ? 'All' : 'Active',
        employmentType: ''
    });
    const [viewMode, setViewMode] = useState('grid'); // Default to grid layout

    useEffect(() => {
        fetchEmployees();
    }, []);

    useEffect(() => {
        applyClientFilters(allRawEmployees);
    }, [filters, searchTerm, allRawEmployees]);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            // Fetch all non-deleted employees (both Active and Inactive) so we can tab between them
            const response = await getEmployees({ includeInactive: true });

            if (response.success && response.data) {
                let rawList = response.data;

                // Restrict to allowed departments based on visibility utility
                const isDeptOrProdHead = user?.role === 'dept_head' || user?.role === 'production_head';
                if (isDeptOrProdHead) {
                    rawList = rawList.filter(emp =>
                        isDeptVisibleToUser(emp.department, emp.department, user)
                    );
                }

                // Compute counts
                const activeCount = rawList.filter(e => e.isActive === true || e.status === 'Active').length;
                const inactiveCount = rawList.filter(e => e.isActive === false || e.status === 'Inactive').length;

                setCounts({
                    active: activeCount,
                    inactive: inactiveCount,
                    total: rawList.length
                });

                setAllRawEmployees(rawList);
                applyClientFilters(rawList);
            } else {
                setAllRawEmployees([]);
                setEmployees([]);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
            toast.error(error.message || 'Failed to fetch employees');
            setAllRawEmployees([]);
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    const applyClientFilters = (sourceList) => {
        let filtered = [...sourceList];

        // 1. Status Filter
        if (filters.status && filters.status !== 'All') {
            if (filters.status === 'Active') {
                filtered = filtered.filter(emp => emp.isActive === true || emp.status === 'Active');
            } else if (filters.status === 'Inactive') {
                filtered = filtered.filter(emp => emp.isActive === false || emp.status === 'Inactive');
            }
        }

        // 2. Search Filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(emp =>
                emp.firstName?.toLowerCase().includes(term) ||
                emp.lastName?.toLowerCase().includes(term) ||
                emp.email?.toLowerCase().includes(term) ||
                emp.employeeId?.toLowerCase().includes(term) ||
                (emp.personal?.fullName || '').toLowerCase().includes(term)
            );
        }

        // 3. Department Filter
        if (filters.department) {
            filtered = filtered.filter(emp =>
                (emp.department || '').toLowerCase() === filters.department.toLowerCase()
            );
        }

        // 4. Employment Type Filter
        if (filters.employmentType) {
            filtered = filtered.filter(emp =>
                emp.employmentType === filters.employmentType
            );
        }

        // 5. Incomplete profiles filter
        if (location.state?.filter === 'incomplete') {
            filtered = filtered.filter(emp => {
                const hasBasicInfo = emp.firstName && emp.lastName && emp.email;
                const hasJobInfo = emp.department && emp.designation;
                const hasDocuments = emp.documents && Array.isArray(emp.documents) && emp.documents.length > 0;
                const isComplete = emp.isProfileComplete === true || (hasBasicInfo && hasJobInfo && hasDocuments);
                return !isComplete;
            });
        }

        setEmployees(filtered);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        applyClientFilters(allRawEmployees);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleViewEmployee = (id) => {
        navigate(`/employees/${id}`);
    };

    const handleEditEmployee = (id) => {
        navigate(`/employees/${id}/edit`);
    };

    const handleToggleStatus = async (employee) => {
        const isCurrentlyActive = employee.isActive === true || employee.status === 'Active';
        const newStatusText = isCurrentlyActive ? 'Inactive' : 'Active';
        const action = isCurrentlyActive ? 'deactivate' : 'activate';

        try {
            await updateEmployeeStatus(employee.id, newStatusText, employee.email);
            toast.success(`Employee ${employee.firstName} ${employee.lastName} ${action}d successfully`);
            await fetchEmployees();
        } catch (error) {
            console.error(`Error ${action}ing employee:`, error);
            toast.error(error.message || `Failed to ${action} employee`);
        }
    };

    const handleDeleteEmployee = async (id) => {
        try {
            await deleteEmployee(id);
            toast.success('Employee deleted successfully');
            fetchEmployees();
        } catch (error) {
            console.error('Error deleting employee:', error);
            toast.error(error.message || 'Failed to delete employee');
        }
    };

    const getStatusBadge = (isActive) => {
        return (
            <Badge bg={isActive ? 'success' : 'secondary'} className="px-2 py-1">
                {isActive ? '● Active' : '○ Inactive'}
            </Badge>
        );
    };

    return (
        <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
            <Sidebar />
            <div className="flex-grow-1 employee-main-content">
                <Navbar />
                <Container fluid className="py-3 py-md-4 px-2 px-md-4">
                    <Row className="mb-3 mb-md-4 align-items-center g-2">
                        <Col xs={12} md={7}>
                            <h2 className="fw-bold text-dark mb-1 fs-4 fs-md-2">Employee Management</h2>
                            <p className="text-muted mb-0 small">Manage organization staff, status, and department assignments</p>
                        </Col>
                        <Col xs={12} md={5} className="d-flex align-items-center justify-content-between justify-content-md-end gap-2 mt-2 mt-md-0 flex-nowrap">
                            <div className="employee-view-toggle">
                                <Button 
                                    variant={viewMode === 'grid' ? 'primary' : 'light'} 
                                    size="sm"
                                    onClick={() => setViewMode('grid')}
                                >
                                    <FaThLarge size={12} /> <span className="fw-bold ms-1">Grid</span>
                                </Button>
                                <Button 
                                    variant={viewMode === 'list' ? 'primary' : 'light'} 
                                    size="sm"
                                    onClick={() => setViewMode('list')}
                                >
                                    <FaList size={12} /> <span className="fw-bold ms-1">List</span>
                                </Button>
                            </div>
                            {isPrivilegedUser && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => navigate('/employees/add')}
                                    className="shadow-sm fw-bold px-3 rounded-pill d-inline-flex align-items-center text-nowrap"
                                    style={{ height: '38px', fontSize: '0.84rem' }}
                                >
                                    <FaPlus className="me-1" size={11} /> Add Employee
                                </Button>
                            )}
                        </Col>
                    </Row>

                    {/* Status Tabs Bar with Horizontal Touch Scroll */}
                    <div className="bg-white rounded-4 shadow-sm p-2 mb-3 border">
                        <div className="employee-status-pills">
                            <Button
                                variant={filters.status === 'Active' ? 'primary' : 'light'}
                                size="sm"
                                className="rounded-pill px-3 fw-bold d-flex align-items-center gap-2 text-nowrap"
                                onClick={() => handleFilterChange('status', 'Active')}
                            >
                                <span>Active Staff</span>
                                <Badge bg={filters.status === 'Active' ? 'light' : 'success'} text={filters.status === 'Active' ? 'primary' : 'white'} className="rounded-pill">
                                    {counts.active}
                                </Badge>
                            </Button>
                            <Button
                                variant={filters.status === 'Inactive' ? 'secondary' : 'light'}
                                size="sm"
                                className="rounded-pill px-3 fw-bold d-flex align-items-center gap-2 text-nowrap"
                                onClick={() => handleFilterChange('status', 'Inactive')}
                            >
                                <span>Inactive / Archived</span>
                                <Badge bg={filters.status === 'Inactive' ? 'dark' : 'secondary'} className="rounded-pill">
                                    {counts.inactive}
                                </Badge>
                            </Button>
                            <Button
                                variant={filters.status === 'All' ? 'dark' : 'light'}
                                size="sm"
                                className="rounded-pill px-3 fw-bold d-flex align-items-center gap-2 text-nowrap"
                                onClick={() => handleFilterChange('status', 'All')}
                            >
                                <span>All Staff</span>
                                <Badge bg="light" text="dark" className="border rounded-pill">
                                    {counts.total}
                                </Badge>
                            </Button>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <Row className="mb-3 g-2">
                        <Col xs={12} md={4}>
                            <Form onSubmit={handleSearch}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by name, email, or ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    size="sm"
                                    className="rounded-3"
                                />
                            </Form>
                        </Col>
                        <Col xs={6} md={3}>
                            {user?.role === 'dept_head' ? (
                                <Form.Control
                                    type="text"
                                    value={user?.department || ''}
                                    readOnly
                                    disabled
                                    size="sm"
                                    className="bg-light fw-bold rounded-3"
                                />
                            ) : (
                                <DepartmentSelector
                                    value={filters.department}
                                    onChange={(value) => handleFilterChange('department', value)}
                                    includeAll={true}
                                    activeOnly={false}
                                    size="sm"
                                />
                            )}
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Select
                                value={filters.employmentType}
                                onChange={(e) => handleFilterChange('employmentType', e.target.value)}
                                size="sm"
                                className="rounded-3"
                            >
                                <option value="">All Employment</option>
                                <option value="Full-time">Full-time</option>
                                <option value="Part-time">Part-time</option>
                                <option value="Contract">Contract</option>
                                <option value="Intern">Intern</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={2} className="text-end">
                            <Button variant="outline-secondary" size="sm" className="w-100 rounded-3 fw-bold" onClick={() => {
                                setFilters({ department: '', designation: '', status: 'Active', employmentType: '' });
                                setSearchTerm('');
                            }}>
                                Reset
                            </Button>
                        </Col>
                    </Row>

                    {/* Employee Table */}
                    <div className="bg-white rounded-4 shadow-sm p-2 p-md-3 border">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2">Loading employees...</p>
                            </div>
                        ) : (
                            <>
                                {viewMode === 'list' ? (
                                    <Table hover responsive className="align-middle text-nowrap mb-0">
                                        <thead>
                                            <tr>
                                                <th>Employee ID</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Department</th>
                                                <th>Designation</th>
                                                <th>Joining Date</th>
                                                {(filters.status === 'Inactive' || filters.status === 'All') && (
                                                    <th>Inactive Date</th>
                                                )}
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {employees.length === 0 ? (
                                                <tr>
                                                    <td colSpan={(filters.status === 'Inactive' || filters.status === 'All') ? 9 : 8} className="text-center py-4">
                                                        No employees found
                                                    </td>
                                                </tr>
                                            ) : (
                                                employees.map((employee) => (
                                                    <tr key={employee.id}>
                                                        <td>{employee.employeeId || employee.id}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                {(employee.photoURL || employee.personal?.photo) ? (
                                                                    <img
                                                                        src={employee.photoURL || employee.personal?.photo}
                                                                        alt={`${employee.firstName} ${employee.lastName}`}
                                                                        className="rounded-circle me-2"
                                                                        style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        className="rounded-circle d-flex align-items-center justify-content-center me-2 shadow-sm"
                                                                        style={{ 
                                                                            width: '32px', 
                                                                            height: '32px', 
                                                                            fontSize: '0.85rem', 
                                                                            fontWeight: '800',
                                                                            backgroundColor: '#e8eaf6',
                                                                            color: '#1a237e',
                                                                            border: '1px solid #c5cae9'
                                                                        }}
                                                                    >
                                                                        {(employee.firstName?.charAt(0) || '') + (employee.lastName?.charAt(0) || '')}
                                                                    </div>
                                                                )}
                                                                <span className="fw-bold">{employee.firstName} {employee.lastName}</span>
                                                            </div>
                                                        </td>
                                                        <td>{employee.email}</td>
                                                        <td>{employee.department}</td>
                                                        <td>{employee.designation}</td>
                                                        <td>
                                                            <span className="small fw-semibold text-secondary">
                                                                {formatDate(employee.joiningDate || employee.job?.joiningDate || employee.jobDetails?.joinDate)}
                                                            </span>
                                                        </td>
                                                        {(filters.status === 'Inactive' || filters.status === 'All') && (
                                                            <td>
                                                                {(!employee.isActive || employee.status === 'Inactive') ? (
                                                                    <span className="small fw-bold text-danger">
                                                                        {formatDate(employee.inactiveDate || employee.exitDate || employee.deactivatedAt || employee.updatedAt)}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted small">—</span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td>{getStatusBadge(employee.isActive)}</td>
                                                        <td>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="me-1"
                                                                onClick={() => handleViewEmployee(employee.id)}
                                                                title="View Details"
                                                            >
                                                                <FaEye />
                                                            </Button>
                                                            {isPrivilegedUser && (
                                                                <>
                                                                    <Button
                                                                        variant="outline-warning"
                                                                        size="sm"
                                                                        className="me-1"
                                                                        onClick={() => handleEditEmployee(employee.id)}
                                                                        title="Edit Employee"
                                                                    >
                                                                        <FaEdit />
                                                                    </Button>
                                                                    <Button
                                                                        variant={employee.isActive ? 'outline-success' : 'outline-secondary'}
                                                                        size="sm"
                                                                        className="me-1"
                                                                        onClick={() => handleToggleStatus(employee)}
                                                                        title={employee.isActive ? 'Deactivate' : 'Activate'}
                                                                    >
                                                                        {employee.isActive ? <FaToggleOn /> : <FaToggleOff />}
                                                                    </Button>
                                                                    {user?.role === 'admin' && (
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => handleDeleteEmployee(employee.id)}
                                                                            title="Delete Employee"
                                                                        >
                                                                            <FaTrash />
                                                                        </Button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <Row className="g-2 g-md-3">
                                        {employees.length === 0 ? (
                                            <Col xs={12} className="text-center py-5 text-muted">No employees found</Col>
                                        ) : (
                                            employees.map((employee) => (
                                                <Col key={employee.id} xs={6} sm={6} lg={4} xl={3}>
                                                    <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden position-relative employee-card">
                                                        <div className="p-2 p-sm-3 p-md-4 text-center employee-card-body">
                                                            <div className="mb-2 mb-md-3 position-relative d-inline-block employee-avatar-wrapper">
                                                                {(employee.photoURL || employee.personal?.photo) ? (
                                                                    <img
                                                                        src={employee.photoURL || employee.personal?.photo}
                                                                        alt={employee.firstName}
                                                                        className="rounded-circle shadow-sm border border-2 border-white employee-avatar-img"
                                                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto shadow-sm employee-avatar-fallback"
                                                                        style={{ 
                                                                            width: '80px', 
                                                                            height: '80px', 
                                                                            fontSize: '2rem', 
                                                                            backgroundColor: '#e8eaf6', 
                                                                            color: '#1a237e',
                                                                            fontWeight: '800',
                                                                            border: '2px solid #c5cae9',
                                                                            textShadow: 'none'
                                                                        }}
                                                                    >
                                                                        {(employee.firstName?.charAt(0) || '') + (employee.lastName?.charAt(0) || '') || <FaUserCircle />}
                                                                    </div>
                                                                )}
                                                                <div className="position-absolute bottom-0 end-0">
                                                                    <div className="employee-status-indicator" style={{ 
                                                                        width: '12px', 
                                                                        height: '12px', 
                                                                        backgroundColor: employee.isActive ? '#28a745' : '#dc3545',
                                                                        borderRadius: '50%',
                                                                        border: '2px solid white'
                                                                    }}></div>
                                                                </div>
                                                            </div>
                                                            <h5 className="fw-bold mb-1 employee-name text-truncate" title={`${employee.firstName} ${employee.lastName}`}>
                                                                {employee.firstName} {employee.lastName}
                                                            </h5>
                                                            <p className="text-muted small mb-1 mb-md-2 employee-designation text-truncate">
                                                                {employee.designation || 'Staff Member'}
                                                            </p>
                                                            <div className="mb-2 mb-md-3">
                                                                <Badge bg="light" text="dark" className="border px-2 py-1 rounded-pill small fw-normal employee-dept-badge text-truncate d-inline-block" style={{ maxWidth: '100%' }}>
                                                                    {employee.department}
                                                                </Badge>
                                                                <div className="mt-1 small text-muted employee-id-text text-truncate">ID: {employee.employeeId || employee.id}</div>

                                                                {/* Joining Date & Inactive Date below ID */}
                                                                {(!employee.isActive || employee.status === 'Inactive') ? (
                                                                    <div className="mt-2 pt-2 border-top border-light-subtle d-flex flex-column gap-1 text-center employee-date-info">
                                                                        <div className="d-flex align-items-center justify-content-center gap-1 text-truncate text-muted" title={`Joining Date: ${formatDate(employee.joiningDate || employee.job?.joiningDate || employee.jobDetails?.joinDate)}`}>
                                                                            <FaCalendarCheck className="text-secondary opacity-75 flex-shrink-0" size={11} />
                                                                            <span>Joined: <strong className="text-dark">{formatDate(employee.joiningDate || employee.job?.joiningDate || employee.jobDetails?.joinDate)}</strong></span>
                                                                        </div>
                                                                        <div className="d-flex align-items-center justify-content-center gap-1 text-truncate text-danger" title={`Inactive Date: ${formatDate(employee.inactiveDate || employee.exitDate || employee.deactivatedAt || employee.updatedAt)}`}>
                                                                            <FaCalendarTimes className="text-danger opacity-75 flex-shrink-0" size={11} />
                                                                            <span>Inactive: <strong>{formatDate(employee.inactiveDate || employee.exitDate || employee.deactivatedAt || employee.updatedAt)}</strong></span>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="mt-2 pt-2 border-top border-light-subtle d-flex align-items-center justify-content-center gap-1 text-truncate text-muted employee-date-info" title={`Joining Date: ${formatDate(employee.joiningDate || employee.job?.joiningDate || employee.jobDetails?.joinDate)}`}>
                                                                        <FaCalendarCheck className="text-success opacity-75 flex-shrink-0" size={11} />
                                                                        <span>Joined: <strong className="text-dark">{formatDate(employee.joiningDate || employee.job?.joiningDate || employee.jobDetails?.joinDate)}</strong></span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="d-flex justify-content-center gap-1 gap-md-2 border-top pt-2 pt-md-3 employee-actions-row flex-wrap">
                                                                <Button variant="light" size="sm" className="rounded-circle employee-action-btn d-flex align-items-center justify-content-center" onClick={() => handleViewEmployee(employee.id)} title="View Detail">
                                                                    <FaEye className="text-primary" />
                                                                </Button>
                                                                {isPrivilegedUser && (
                                                                    <>
                                                                        <Button variant="light" size="sm" className="rounded-circle employee-action-btn d-flex align-items-center justify-content-center" onClick={() => handleEditEmployee(employee.id)} title="Edit">
                                                                            <FaEdit className="text-warning" />
                                                                        </Button>
                                                                        <Button variant="light" size="sm" className="rounded-circle employee-action-btn d-flex align-items-center justify-content-center" onClick={() => handleToggleStatus(employee)} title={employee.isActive ? 'Deactivate' : 'Activate'}>
                                                                            {employee.isActive ? <FaToggleOn className="text-success" /> : <FaToggleOff className="text-secondary" />}
                                                                        </Button>
                                                                        {user?.role === 'admin' && (
                                                                            <Button variant="light" size="sm" className="rounded-circle employee-action-btn d-flex align-items-center justify-content-center" onClick={() => handleDeleteEmployee(employee.id)} title="Delete">
                                                                                <FaTrash className="text-danger" />
                                                                            </Button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))
                                        )}
                                    </Row>
                                )}
                                
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="small text-muted fw-bold">
                                        Total {employees.length} Staff Member{employees.length !== 1 ? 's' : ''} Listed
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Container>
            </div>
        </div>
    );
};

export default EmployeeListPage;
